import React, {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SelectionController from 'components/SelectionController/SelectionController';
import SelectableItem from 'javascripts/dnd/SelectableItem';
import {
  RECORD_CLASSES,
  type ReferenceRecord,
  defaultScope,
  fieldsForItem,
} from './pillFields';
import drawerCapableClasses from 'javascripts/constants/drawerCapableClasses.json';
import AhaDrawer from 'javascripts/drawer';
import { ReferenceAhaContextMenu } from './ReferenceAhaContextMenu';
import type BatchedRecordLoader from 'components/framework/BatchedRecordLoader';
import ReferenceAhaPillFields from './ReferenceAhaPillFields';
import TaskCheckbox from './TaskCheckbox';
import type ApplicationModel from 'components/framework/ApplicationModel';
import {
  runAfterSlate,
  type AhaReferenceElement,
} from '@aha-app/aha-editor-v2';

export type UpdateReferenceOptions = (options: {
  visibleFields: string[];
}) => void;

function openContextMenu(
  event: MouseEvent,
  element: AhaReferenceElement,
  onUpdateReferenceOptions: UpdateReferenceOptions,
  record: ReferenceRecord | null,
  isModifiable: boolean
) {
  const ModelClass = RECORD_CLASSES[element.reference_class];
  if (!ModelClass) {
    return;
  }

  // Create a fake record if it's not loaded
  if (!record) {
    const path = new URL(element.reference_url).pathname;
    record = new ModelClass({
      id: element.reference_id,
      referenceNum: element.reference_num || element.reference_id,
      name: element.reference_name,
      path,
    });
  }

  let customActionRender;
  if (isModifiable) {
    customActionRender = () => (
      <ReferenceAhaContextMenu
        element={element}
        onUpdateReferenceOptions={onUpdateReferenceOptions}
      />
    );
  }

  const selectableItem = new SelectableItem<any, null>({
    item: record,
    controller: null,
  });
  SelectionController.openContextMenuForItems(
    [selectableItem],
    event.pageX,
    event.pageY,
    {
      actions: ['CopyLinkAction', 'OpenInNewTabAction'],
      hideExtensionActions: true,
      customActionRender,
    }
  );
}

type OpenDrawerCallback = (
  event: React.MouseEvent<HTMLAnchorElement>,
  path: string
) => void;

const openDrawer: OpenDrawerCallback = (event, path) => {
  const body = document.getElementsByTagName('body')[0];
  const isInFullscreen = body && body.classList.contains('aha-fullscreen');

  if (
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey ||
    (!window.featureFlags?.ACT_1949_multiple_drawers && isInFullscreen)
  ) {
    // Replace href before falling back to native behaviour
    event.currentTarget.href = path;

    // Use default browser behaviour, but prevent focusing in the editor
    event.stopPropagation();
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  // In case of clicking on the icon inside the link
  if (AhaDrawer.canOpen()) {
    AhaDrawer.showUrl(path);
  } else {
    // We are in the desktop app which doesn't have a drawer. Just navigate.
    window.location.href = path;
  }
};

type ModelStateOptions = {
  reactive?: Boolean;
};

function useModelState<T extends ApplicationModel>(
  initialState: T | null,
  options: ModelStateOptions = {}
): [T | null, (newState: T | null) => void, Date | null] {
  const [model, setModel] = useState<T | null>(initialState);
  const [modelLastUpdated, setModelLastUpdated] = useState<Date | null>(null);

  const updateState = useCallback(
    newState => {
      setModel(newState);
      setModelLastUpdated(new Date());
    },
    [setModel, setModelLastUpdated]
  );

  useEffect(() => {
    if (options.reactive) {
      if (model) {
        model.reactive({
          onUpdate: model => {
            updateState(model);
          },
        });
      }

      return () => {
        if (model) model.unreactive();
      };
    }
  }, [model, options.reactive, updateState]);

  return [model, updateState, modelLastUpdated];
}
interface ReferenceAhaPillProps {
  batchedRecordLoader: BatchedRecordLoader;
  element: AhaReferenceElement;
  isModifiable: boolean;
  onUpdateReferenceOptions: UpdateReferenceOptions;
}

export const ReferenceAhaPill = ({
  batchedRecordLoader,
  element,
  isModifiable,
  onUpdateReferenceOptions,
  children,
}: PropsWithChildren<ReferenceAhaPillProps>) => {
  const {
    reference_class: itemClass,
    reference_id,
    reference_num,
    reference_url,
  } = element;
  const [record, setRecord] = useModelState<ReferenceRecord>(null, {
    reactive: true,
  });

  // if it turns out element.reference_class could be the decorated version,
  // check for element.reference_class minus "Decorator"
  const drawerCapable = drawerCapableClasses.includes(itemClass);

  const pillPath = useMemo(() => {
    const parsedUrl = new URL(reference_url, window.location.href);
    return parsedUrl.pathname;
  }, [reference_url]);

  let clickHandler: OpenDrawerCallback;
  if (drawerCapable && !window.isPublishing) {
    clickHandler = openDrawer;
  } else {
    clickHandler = e => e.stopPropagation(); // prevents editor focus
  }

  useEffect(() => {
    if (window.currentAccount?.onlyNotebooksFlavor) {
      return;
    }

    const modelClass = RECORD_CLASSES[itemClass];
    if (!modelClass || !batchedRecordLoader) {
      setRecord(null);
      return;
    }

    const fieldOptions = fieldsForItem(itemClass);
    const recordId = reference_id || reference_num;

    if (!recordId) {
      setRecord(null);
      return;
    }

    const query = fieldOptions.reduce(
      (scope, option) => option.scope(scope),
      defaultScope(modelClass)
    );

    void batchedRecordLoader.loadRecord(query, recordId).then(record => {
      if (record) {
        setRecord(record as ReferenceRecord);
      }
    });
  }, [itemClass, reference_id, reference_num, batchedRecordLoader, setRecord]);

  return (
    <span>
      {record?.typename === 'Task' && record.type === 'TASK' && (
        <TaskCheckbox record={record} setRecord={setRecord} />
      )}
      <a
        href={pillPath}
        target='_blank'
        onClick={event => clickHandler(event, pillPath)}
        className='aha-reference tw-select-text'
        data-skip-drawer-close={drawerCapable}
        onContextMenu={event => {
          if (!window.isPublishing) {
            const { nativeEvent } = event;

            // Cancel default right-click immediately, but open the menu with a delay
            event.preventDefault();
            event.stopPropagation();

            runAfterSlate(() => {
              openContextMenu(
                nativeEvent,
                element,
                onUpdateReferenceOptions,
                record,
                isModifiable
              );
            });
          }
        }}
      >
        <ReferenceAhaPillFields element={element} record={record} />
      </a>
    </span>
  );
};
